/**
 * `createBridgeMailTransport(api)` — the web runtime's MailTransport over the server IMAP bridge
 * (api.md "IMAP bridge"; contracts/wiring.ts `CreateBridgeMailTransport`). The password is read from the
 * SecretStore by the caller and travels in this one request body; the bridge never stores it. Message
 * sources come back base64 and are decoded here; parsing happens on the device (sources/email.ts).
 */

import type { ImapFetchRequest, ImapFetchResponse, ImapMailboxesRequest, ImapMailboxesResponse } from '../contracts/api.js';
import type { EpochMs } from '../contracts/ids.js';
import type { MailAccount, MailFetchResult, MailTransport, RawMail } from '../contracts/sources.js';
import type { ApiCaller, CreateBridgeMailTransport } from '../contracts/wiring.js';
import { DigesterError } from '../kernel/errors.js';

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INDEX: Record<string, number> = Object.fromEntries([...B64].map((c, i) => [c, i]));

/** Standard base64 (padding optional, whitespace ignored) → bytes. Self-contained: no atob, no Buffer. */
export function base64ToBytes(input: string): Uint8Array {
  const clean = input.replace(/[\s=]+/g, '');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let bits = 0;
  let value = 0;
  let o = 0;
  for (const ch of clean) {
    const v = B64_INDEX[ch];
    if (v === undefined) throw new DigesterError('parse_failed', 'The mail bridge sent a message that is not valid base64');
    value = (value << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (value >> bits) & 0xff;
    }
  }
  return out.subarray(0, o);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function malformed(route: string): DigesterError {
  return new DigesterError('fetch_failed', `The mail bridge sent an unexpected response to ${route}`);
}

function dto(account: MailAccount): ImapMailboxesRequest['account'] {
  return { host: account.host, port: account.port, security: account.security, user: account.user, password: account.password };
}

export const createBridgeMailTransport: CreateBridgeMailTransport = (api: ApiCaller): MailTransport => ({
  async listMailboxes(account: MailAccount): Promise<string[]> {
    const body: ImapMailboxesRequest = { account: dto(account) };
    const res = await api.call<ImapMailboxesResponse>('imapMailboxes', { body });
    if (!isObject(res) || !Array.isArray(res.mailboxes) || !res.mailboxes.every((m) => typeof m === 'string')) throw malformed('imapMailboxes');
    return res.mailboxes;
  },

  async fetchSince(account, mailbox, sinceUid, uidValidity, limit): Promise<MailFetchResult> {
    const body: ImapFetchRequest = { account: dto(account), mailbox, sinceUid, uidValidity, limit };
    const res = await api.call<ImapFetchResponse>('imapFetch', { body });
    if (!isObject(res) || typeof res.uidValidity !== 'number' || !Array.isArray(res.messages) || typeof res.more !== 'boolean') {
      throw malformed('imapFetch');
    }
    const messages: RawMail[] = res.messages.map((m) => {
      if (!isObject(m) || typeof m.uid !== 'number' || typeof m.internalDate !== 'number' || typeof m.rfc822 !== 'string' || typeof m.truncated !== 'boolean') {
        throw malformed('imapFetch');
      }
      return { uid: m.uid, internalDate: m.internalDate as EpochMs, rfc822: base64ToBytes(m.rfc822), truncated: m.truncated };
    });
    return { uidValidity: res.uidValidity, messages, more: res.more };
  },
});
